import {
  AccountRecord,
  PaymentMethodAccountMappingRecord,
  PaymentMethodRecord,
} from '@/renderer/types/accounting';
import { Button } from '@components/common';
import React from 'react';

interface PaymentMethodMappingsTableProps {
  paymentMethods: PaymentMethodRecord[];
  mappings: PaymentMethodAccountMappingRecord[];
  accounts: AccountRecord[];
  onEdit: (
    mapping: PaymentMethodAccountMappingRecord | null,
    method: PaymentMethodRecord,
  ) => void;
  onDelete: (mapping: PaymentMethodAccountMappingRecord) => void;
}

const formatPaymentMethodType = (value: PaymentMethodRecord['type']) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export function PaymentMethodMappingsTable({
  paymentMethods,
  mappings,
  accounts,
  onEdit,
  onDelete,
}: PaymentMethodMappingsTableProps) {
  const accountById = new Map(accounts.map((account) => [account.id, account]));
  const mappingByMethodId = new Map(
    mappings.map((mapping) => [mapping.payment_method_id, mapping]),
  );

  return (
    <div className="max-h-[560px] overflow-auto rounded-2xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Payment method</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Mapped account</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {paymentMethods.map((method) => {
            const mapping = mappingByMethodId.get(method.id) || null;
            const account = mapping
              ? accountById.get(mapping.account_id)
              : null;

            return (
              <tr key={method.id}>
                <td className="px-4 py-3 font-semibold text-gray-900">
                  {method.name}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {formatPaymentMethodType(method.type)}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {account
                    ? `${account.code} - ${account.name}`
                    : 'No account mapped'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => onEdit(mapping, method)}
                    >
                      {mapping ? 'Edit' : 'Map'}
                    </Button>
                    {mapping ? (
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(mapping)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
